import React, {FC, ReactElement, useCallback, useMemo, useState} from "react";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import classnames from "classnames";
import {useQuery} from "@apollo/client";

import {AnalyticsCard} from "./card";
import {Card} from "../../components/card";
import {
	DateRange,
	DateRangeOptions,
	DateRangeType,
	useCustomRange,
} from "../../components/input/date-time/date-range";
import {useGroupList} from "../../data";
import {Button, InputRow, Option, Select, Separator} from "../../components/input";
import {GradientChart} from "../../components/chart/gradient-chart";
import {Icon} from "../../components/images";
import {Span} from "../../components/text";
import {defaultLoadingProps as loadingProps, usePaginatedQuery} from "../../paginated-query";
import {usePageInput, usePageScroll} from "../../layout";
import {downloadCsv} from "../../utils/csv";
import {
	AdoptionStatus,
	getUserRank,
	UserAdoption,
	GET_USER_ADOPTION_STATS,
	GET_USERS_ADOPTION,
	GET_TOTAL_ACTIVE_USERS,
} from "../../data/user";

import styles from "./adoption.module.scss";

type SortDirection = "ASC" | "DESC";

const adoptionStatuses = [
	{label: "All statuses", value: "ALL"},
	{label: "Accepted invitations", value: "ACCEPTED"},
	{label: "Pending", value: "INVITED"},
	{label: "Shared Once", value: "SHARED_ONCE"},
	{label: "Shared Multiple Times", value: "SHARED_MULTIPLE"},
] as const;

const readableStatus = (status: AdoptionStatus): string =>
	status === "INVITED"
		? "Pending"
		: status === "SHARED_MULTIPLE"
		? "Shared Multiple Times"
		: status === "SHARED_ONCE"
		? "Shared Once"
		: "Accepted";

const UserRow: FC<{userAdoption: UserAdoption}> = ({userAdoption}) => {
	const groups = useGroupList();

	return (
		<div className={classnames(styles.list, styles.listItem)}>
			<Span>{userAdoption.fullName}</Span>
			<Span>{userAdoption.email}</Span>
			<Span>
				{groups
					.filter(group => userAdoption.groups.includes(group.value))
					.map(group => group.label)
					.join(", ")}
			</Span>
			<Span>{readableStatus(userAdoption.status)}</Span>
			<Span>{userAdoption.rank || "-"}</Span>
			<Span>{getUserRank(userAdoption.experience) || "-"}</Span>
		</div>
	);
};

const minDate = dayjs().add(-10, "year").startOf("year");

interface AdoptionFilter {
	groups: number[];
	status: AdoptionStatus | "ALL";
	sort: SortDirection;
}

const defaultFilter: AdoptionFilter = {
	groups: [],
	status: "ALL",
	sort: "ASC",
};

export function Adoption(): ReactElement {
	const navigate = useNavigate();
	const [filter, setFilter] = useState(defaultFilter);
	const groups = useGroupList();
	const [customDate, setCustomDate] = useState<DateRangeType>(DateRangeOptions[0].value);
	const {start, end} = useCustomRange(customDate, minDate);
	const {data, loading} = useQuery(GET_USER_ADOPTION_STATS, {variables: {start, end}});
	const {data: totalActiveUsers} = useQuery(GET_TOTAL_ACTIVE_USERS);
	const adoptionStats = useMemo(() => (!loading ? data.adoptionStats : []), [data, loading]);

	const rangeDifference = useMemo(
		() =>
			end.diff(start, "year") >= 1
				? "year"
				: end.diff(start, "month") >= 1
				? "month"
				: end.diff(start, "week") >= 1
				? "week"
				: "day",
		[start, end]
	);

	const sharedUsersDates = useMemo(
		() =>
			adoptionStats
				.filter(user => user.status === "SHARED_ONCE" || user.status === "SHARED_MULTIPLE")
				.map(h => ({
					created: dayjs(h.created).startOf(rangeDifference).startOf("day").unix(),
					status: h.status,
				})),
		[adoptionStats, rangeDifference]
	);
	const invitedUsersDates = useMemo(
		() =>
			adoptionStats
				.filter(user => user.status === "INVITED")
				.map(h => ({created: dayjs(h.created).startOf(rangeDifference).startOf("day").unix()})),
		[adoptionStats, rangeDifference]
	);
	const handleExport = useCallback(
		(data: UserAdoption[]) => {
			const columns = ["Name", "Email", "Groups", "Status", "Rank", "Experience"];
			const csvData = data.map(row => [
				row.fullName,
				row.email,
				row.groups.map(group => groups.find(g => g.value === group)?.value).join(", "),
				readableStatus(row.status),
				row.rank || "-",
				row.experience || "-",
			]);

			const exportFileName = `${
				filter.status === "ACCEPTED"
					? "accepted"
					: filter.status === "SHARED_ONCE"
					? "shared_once"
					: filter.status === "SHARED_MULTIPLE"
					? "shared_multiple"
					: "all_users"
			}.csv`;

			downloadCsv(csvData, columns, exportFileName);
		},
		[groups, filter.status]
	);

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h3>User Adoption</h3>
				<InputRow>
					<DateRange
						min={minDate}
						max={dayjs().add(2, "day").startOf("day")}
						value={customDate}
						onChange={setCustomDate}
					/>
				</InputRow>
			</div>
			<div className={styles.analyticsCards}>
				<AnalyticsCard
					value={adoptionStats.filter(user => user.status === "INVITED").length}
					label="Pending"
				/>
				<AnalyticsCard
					value={adoptionStats.filter(user => user.status === "ACCEPTED").length}
					label="Accepted"
				/>
				<AnalyticsCard
					value={adoptionStats.filter(user => user.status === "SHARED_ONCE").length}
					label="Shared Once"
				/>
				<AnalyticsCard
					value={adoptionStats.filter(user => user.status === "SHARED_MULTIPLE").length}
					label="Shared Multiple times"
				/>
			</div>
			<div className={styles.chartContainer}>
				<Card column className={styles.chart}>
					<h3>Invited Users</h3>
					<GradientChart
						subtitle="Number of users invited"
						data={[
							{
								data: invitedUsersDates
									.map((date, index, curr) => ({
										value: curr.filter(d => d.created === date.created).length,
										duplicate: curr.findIndex(d => d.created === date.created) !== index,
									}))
									.filter(val => !val.duplicate)
									.map(val => val.value),
								color: "blue-100",
							},
						]}
						labels={invitedUsersDates
							.filter((val, index, curr) => curr.findIndex(d => d.created === val.created) === index)
							.map(c =>
								dayjs
									.unix(c.created)
									.format(
										rangeDifference === "day" || rangeDifference === "week"
											? "MMM DD"
											: rangeDifference === "month"
											? "MMM YYYY"
											: "YYYY"
									)
							)}
						showLegend={false}
						chartName="Invited Users"
					/>
					<Span color="grey" className={styles.label}>
						{end.isSame(undefined, "day") ? start.fromNow() : "Custom Range"}
					</Span>
				</Card>
				<Card column className={styles.chart}>
					<h3>Users Who Have Shared</h3>
					<GradientChart
						data={[
							{
								data: sharedUsersDates
									.map((date, index, curr) => ({
										value: curr.filter(d => d.created === date.created && d.status === "SHARED_ONCE").length,
										duplicate:
											curr.findIndex(d => d.created === date.created && d.status === "SHARED_ONCE") !== index,
									}))
									.filter(val => !val.duplicate)
									.map(val => val.value),
								label: "Shared Once",
								color: "blue-100",
							},
							{
								data: sharedUsersDates
									.map((date, index, curr) => ({
										value: curr.filter(d => d.created === date.created && d.status === "SHARED_MULTIPLE")
											.length,
										duplicate:
											curr.findIndex(d => d.created === date.created && d.status === "SHARED_MULTIPLE") !==
											index,
									}))
									.filter(val => !val.duplicate)
									.map(val => val.value),
								label: "Shared Multiple Times",
								color: "blue-primary",
							},
						]}
						labels={sharedUsersDates
							.filter((val, index, curr) => curr.findIndex(d => d.created === val.created) === index)
							.map(c =>
								dayjs
									.unix(c.created)
									.format(
										rangeDifference === "day" || rangeDifference === "week"
											? "MMM DD"
											: rangeDifference === "month"
											? "MMM YYYY"
											: "YYYY"
									)
							)}
						showLegend
						chartName="Shared Users"
					/>
					<Span color="grey" className={styles.label}>
						{end.isSame(undefined, "day") ? start.fromNow() : "Custom Range"}
					</Span>
				</Card>
			</div>
			<div className={styles.organizationMembersContainer}>
				<h3>Organization Members</h3>
				<div className={classnames(styles.header, styles.compact)}>
					<Span bold className={styles.inlineSpan}>
						View and manage full list of users in your organization in
					</Span>
					<Span bold color="blue" onClick={() => navigate("/settings/users")}>
						User management in Settings
					</Span>
				</div>
			</div>

			<PaginatedList
				filter={filter}
				handleExport={handleExport}
				setFilter={setFilter}
				groups={groups}
				totalActiveUsers={totalActiveUsers?.totalActiveUsers ?? 0}
			/>
		</div>
	);
}

const collator = new Intl.Collator("en-US");

const renderItem = row => <UserRow userAdoption={row} />;

const empty = (
	<InputRow position="center">
		<Span>No users found.</Span>
	</InputRow>
);

export const PaginatedList: FC<{
	filter: AdoptionFilter;
	groups: Option<number>[];
	totalActiveUsers: number;
	handleExport: (users: UserAdoption[]) => void;
	setFilter: (f: AdoptionFilter) => void;
}> = ({groups, filter, totalActiveUsers, handleExport, setFilter}) => {
	const variables = useMemo(
		() => ({
			groups: filter.groups.length ? filter.groups : undefined,
			status: filter.status === "ALL" ? undefined : filter.status,
		}),
		[filter.groups, filter.status]
	);
	const sortItems = useCallback(
		(a: UserAdoption, b: UserAdoption) =>
			filter.sort === "ASC"
				? collator.compare(a.fullName.toUpperCase(), b.fullName.toUpperCase())
				: collator.compare(b.fullName.toUpperCase(), a.fullName.toUpperCase()),
		[filter.sort]
	);
	const {handleScroll, render, data} = usePaginatedQuery<UserAdoption>(GET_USERS_ADOPTION, {
		loadingProps,
		variables,
		sortItems,
		renderItem,
		empty,
	});
	usePageScroll(handleScroll);

	usePageInput(
		() => (
			<Button disabled={data.length === 0} icon="export" value="Export" onClick={() => handleExport(data)} />
		),
		[handleExport, data]
	);

	return (
		<>
			<InputRow position="between">
				<InputRow>
					<Span>
						Displaying {data?.length} of {totalActiveUsers} users
					</Span>
				</InputRow>
				<InputRow>
					<Select
						multi
						pills
						options={groups}
						value={filter.groups}
						placeholder="All Groups"
						onChange={g => setFilter({...filter, groups: g})}
					/>
					<Select
						options={adoptionStatuses}
						value={filter.status}
						onChange={s => setFilter({...filter, status: s})}
					/>
				</InputRow>
			</InputRow>
			<Card>
				<div className={styles.userList}>
					<div className={classnames(styles.list, styles.listHeader)}>
						<div className={styles.nameContainer}>
							<Span bold>Name</Span>
							<Icon
								icon="sort"
								color="black"
								className={styles[filter.sort.toLowerCase()]}
								onClick={() => setFilter({...filter, sort: filter.sort === "ASC" ? "DESC" : "ASC"})}
							/>
						</div>
						<Span bold>E-mail</Span>
						<Span bold>Group</Span>
						<Span bold>Status</Span>
						<Span bold>Rank</Span>
						<Span bold>Level</Span>
					</div>
					<Separator horizontal />
					{render}
				</div>
			</Card>
		</>
	);
};
